/**
 * Extra hours and cancelled days, from the server.
 *
 * These were `doctor.extraHours` and `doctor.cancelledDays` on the legacy
 * doctor object, saved through `window.saveData()` — whose storage writes were
 * every one of them commented out (app.js:3962), so they lived in one tab's
 * memory and were gone on refresh. There were no columns for them either, so
 * the patient's calendar could never show an evening the doctor had opened.
 *
 * They are now DoctorExtraHours and DoctorCancelledDays rows, reachable at
 * /api/doctors/extra-hours and /api/doctors/cancelled-days. Both read routes
 * are public, keyed on doctorId, because the patient calendar needs them; the
 * writes require the doctor's own token.
 *
 * Closing a day cancels that day's bookings server-side, inside one
 * transaction — `cancelDay` returns how many, so the caller can say so.
 */

import { useCallback, useMemo } from 'react';
import { doctorApi } from '../../../api/endpoints';
import { useAsync } from '../../../hooks/useAsync';
import { normalizeExtraHours } from '../../../domain/catalog';

export function useDayOverrides(doctorId) {
  const extraHoursState = useAsync(() => doctorApi.extraHours(doctorId), [doctorId], {
    initialData: [],
    enabled: Boolean(doctorId),
  });

  const cancelledState = useAsync(() => doctorApi.cancelledDays(doctorId), [doctorId], {
    initialData: [],
    enabled: Boolean(doctorId),
  });

  const extraHours = useMemo(
    () => (extraHoursState.data || []).map(normalizeExtraHours).filter(Boolean),
    [extraHoursState.data],
  );

  const cancelledDays = useMemo(
    () =>
      (cancelledState.data || [])
        .map((entry) => (entry?.date ? String(entry.date).slice(0, 10) : null))
        .filter(Boolean),
    [cancelledState.data],
  );

  /** Add or replace one day's block. An empty `slots` array removes it. */
  const saveExtraHours = useCallback(
    async (payload) => {
      const result = await doctorApi.saveExtraHours(payload);
      await extraHoursState.reload();
      return result;
    },
    [extraHoursState],
  );

  /** Close a day. Resolves to the number of bookings the server cancelled. */
  const cancelDay = useCallback(
    async (dateKey, reason) => {
      const result = await doctorApi.cancelDay(dateKey, reason);
      await cancelledState.reload();
      return result?.cancelledBookings ?? 0;
    },
    [cancelledState],
  );

  /** Reopen a day. The bookings cancelled with it are not restored. */
  const reopenDay = useCallback(
    async (dateKey) => {
      await doctorApi.reopenDay(dateKey);
      await cancelledState.reload();
    },
    [cancelledState],
  );

  return {
    extraHours,
    cancelledDays,
    loading: extraHoursState.loading || cancelledState.loading,
    error: extraHoursState.error || cancelledState.error,
    saveExtraHours,
    cancelDay,
    reopenDay,
    reload: () => {
      extraHoursState.reload();
      cancelledState.reload();
    },
  };
}

export default useDayOverrides;
